<template>
  <div class="container">
    <Toolbar class="mt-2 mb-2">
      <template #start>
        <h2>
          <span v-if="isUpdate">
            {{ $t('officepage.updateOffice') }}
          </span>
          <span v-else>{{ $t('officepage.createOffice') }}</span>
        </h2>
      </template>
    </Toolbar>
    <form @submit.prevent="submit">
      <div class="p-input mb-3">
        <label
          for="site"
          class="required-input"
        >{{ $t('entity.office.site') }}</label>
        <strong v-if="isUpdate"> : {{ selectedSite(v$.office.siteId.$model) }}</strong>
        <Dropdown
          v-if="!isUpdate"
          id="site"
          v-model="v$.office.siteId.$model"
          :options="sites"
          :option-label="(site) => `${site.code} - ${site.name} - ${site.address.postcode}`"
          option-value="id"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.office.siteId.$error }"
        />
        <div
          v-for="error of v$.office.siteId.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>
      <div class="p-input mb-3">
        <label
          for="name"
          class="required-input"
        >{{ $t('entity.office.name') }}</label>
        <InputText
          id="name"
          v-model="v$.office.name.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.office.name.$error }"
          type="text"
        />
        <div
          v-for="error of v$.office.name.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="openDate"
          class="required-input"
        >{{ $t('entity.office.openCloseDate') }}</label>
        <Calendar
          id="openDate"
          v-model="selectedDates"
          selection-mode="range"
          class="col-12 p-0 mt-2"
          :class="{'invalid-input': v$.office.openDate.$error || v$.office.closeDate.$error }"
          type="text"
          @date-select="selectDates"
          :manual-input="false"
        />
        <div
          v-for="error of v$.office.openDate.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>

        <div
          v-for="error of v$.office.closeDate.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div
            v-if="v$.office.openDate.$errors.length === 0"
            class="error-msg"
          >
            {{ error.$message }}
          </div>
        </div>
      </div>

      <h2>{{ $t('entity.office.planning') }}</h2>
      <div
        v-if="office.planning"
        class="grid"
      >
        <DayPlanningForm
          id="mondayplanning"
          v-model="v$.office.planning.mondayPlanning.$model"
          :title="$t('entity.planning.monday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <DayPlanningForm
          id="tuesdayplanning"
          v-model="v$.office.planning.tuesdayPlanning.$model"
          :title="$t('entity.planning.tuesday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <DayPlanningForm
          id="wednesdayplanning"
          v-model="v$.office.planning.wednesdayPlanning.$model"
          :title="$t('entity.planning.wednesday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <DayPlanningForm
          id="thursdayplanning"
          v-model="v$.office.planning.thursdayPlanning.$model"
          :title="$t('entity.planning.thursday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <DayPlanningForm
          id="fridayplanning"
          v-model="v$.office.planning.fridayPlanning.$model"
          :title="$t('entity.planning.friday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <DayPlanningForm
          id="saturdayplanning"
          v-model="v$.office.planning.saturdayPlanning.$model"
          :title="$t('entity.planning.saturday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <DayPlanningForm
          id="sundayplanning"
          v-model="v$.office.planning.sundayPlanning.$model"
          :title="$t('entity.planning.sunday')"
          class="col-12 md:col-6 lg:col-3"
        />
      </div>

      <Message
        v-if="errors.length > 0"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error.content"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>

      <div class="my-3 flex justify-content-between flex-wrap">
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="toOfficeTable"
        />
        <Button
          type="submit"
          :label="isUpdate ? $t('action.crud.update') : $t('action.crud.create')"
          icon="pi pi-save"
          class="p-button-rounded p-button-outlined"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import {maxLength, required} from "@/i18n/i18nValidators";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import DayPlanningForm from "@/components/office/DayPlanningForm";
import moment from "moment";
import Calendar from "primevue/calendar";
import Message from "primevue/message";
import Toolbar from "primevue/toolbar";

export default {
  name: 'OfficeForm',
  components: { DayPlanningForm, Toolbar, Dropdown, InputText, Button, Calendar, Message },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      office: {
        id: null,
        siteId: null,
        name: '',
        openDate: null,
        closeDate: null,
        planning: {
          mondayPlanning: null,
          tuesdayPlanning: null,
          wednesdayPlanning: null,
          thursdayPlanning: null,
          fridayPlanning: null,
          saturdayPlanning: null,
          sundayPlanning: null,
        },
      },
      sites: [],
      selectedDates: [],
      isUpdate: false,
      errors: []
    }
  },
  validations() {
    return {
      office: {
        id: {},
        siteId: { required },
        name: { required, maxLength: maxLength(30) },
        openDate: { required },
        closeDate: { required },
        planning: {
          mondayPlanning: {},
          tuesdayPlanning: {},
          wednesdayPlanning: {},
          thursdayPlanning: {},
          fridayPlanning: {},
          saturdayPlanning: {},
          sundayPlanning: {},
        }
      }
    }
  },
  mounted() {
    this.fetchSites()
    if (this.$route.params.id) {
      this.fetchOffice(this.$route.params.id)
      this.isUpdate = true;
    }
  },

  methods: {
    async fetchOffice(officeId) {
      this.office = await this.$officeService.fetchOffice(officeId)
      this.office.siteId = this.office.site.id
      this.selectedDates = [
        new Date(this.office.openDate),
        new Date(this.office.closeDate)
      ]
    },
    async fetchSites() {
      this.sites = (await this.$siteService.fetchSitesPagingAndSorting()).content;
      this.sites = this.sites.slice().sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    },

    selectDates() {
      this.v$.office.openDate.$model = null
      this.v$.office.closeDate.$model = null
      if (this.selectedDates && this.selectedDates.length == 2) {
        if (this.selectedDates[0] !== null) {
          this.v$.office.openDate.$model = moment(this.selectedDates[0]).format('YYYY-MM-DD')
        }

        if (this.selectedDates[1] !== null) {
          this.v$.office.closeDate.$model = moment(this.selectedDates[1]).format('YYYY-MM-DD')
        }
      }
    },
    async submit() {
      const formErrors = await this.v$.$validate()
      if (!formErrors) return

      this.errors = []
      if (this.isUpdate) {
        await this.update()
      } else {
        await this.create()
      }
    },
    toOfficeTable() {
      this.$router.push({ name: 'office' })
    },

    async create() {
      try {
        await this.$officeService.create(this.office)
        this.$emitter.emit('toast', { severity: 'success', message: 'officepage.toast.createSuccess' })
        this.toOfficeTable()
      } catch (err) {
        this.errors = this.$t(err.response.data);
      }
    },

    async update() {
      try {
        await this.$officeService.update(this.office)
        this.$emitter.emit('toast', { severity: 'success', message: 'officepage.toast.updateSuccess' })
        this.toOfficeTable()
      } catch (err) {
        this.errors = this.$t(err.response.data);
      }
    },
    selectedSite(index) {
      let site = this.sites.find((site) => site.id === index);
      return site ? `${site.code} - ${site.name} - ${site.address.postcode}` : "";
    }

  }
}
</script>
